import { usOrderNeedsFillVisibility } from '../../lib/polymarketUs/fillProgress';
/**
 * #989 — the Polymarket US portfolio presenter, for the standalone page
 * (`PolymarketUsView`, unrouted) and the #1002 acceptance suite.
 *
 * An isolated, adapter-driven composition: it takes an injected
 * `UsPortfolioAdapter` (the fixture exchange in tests and the preview, or the
 * browser `UsPortfolioClient` in `UsPortfolioWorkspace`) and the selected
 * scope. See docs/260928PolymarketUsPortfolioConnection.md.
 *
 * On the Position Tracker, US orders and holdings are rows of the shared
 * Open Orders / Open Positions lists (#1226, `usTrackerFeed.tsx`), which
 * keeps the same read, lock, keep and confirm rules as this panel; the row
 * presenters here are the standalone page's only.
 *
 * Selection order matters with the real client: `UsPortfolioClient` refuses a
 * read made before `setSelection` names this account and view
 * (US_CLIENT_CONTEXT_CHANGED). The composing page must select BEFORE this
 * panel's read effect runs: in the event handler that changes the selection,
 * or in a `useLayoutEffect` (layout effects run before a child's effects). A
 * refusal that is not explained by a newer visit is shown, never hidden.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usSectionCoverage } from '@/lib/polymarketUs/coverage';
import {
  isSameUsScope,
  acceptUsSnapshot,
  applyUsCancelResult,
  applyUsOrderStatus,
  presentUsOrder,
  UNAVAILABLE,
  usCancelView,
  usOrderKey,
} from '@/lib/polymarketUs/portfolioModel';
import { decimalUnits } from '@/lib/polymarketUs/ticketModel';
import { usPriceCentsLabel } from '@/lib/polymarketUs/format';
import type { UsOrderRow, UsPortfolioAdapter, UsPortfolioSnapshot, UsPositionRow, UsReadiness, UsScope } from '@/lib/polymarketUs/types';
import { ConfirmModal } from '@/components/atoms/Modal';
import { ACTION_BUTTON, UsOrderTable, type UsOrderActions, type UsOrderOperation } from './UsOrderTable';
import { UsPositionCards } from './UsPositionCards';
import { VENUE_NAME } from './usVenueLabel';

export type UsPortfolioPanelProps = {
  adapter: UsPortfolioAdapter;
  scope: UsScope;
  /** Whose orders are shown. Part of the visit: switching it re-reads. */
  view?: 'own' | 'desk';
  /** The account's name where it can differ (two or more accounts), for the
   * cancel confirmation; '' when there is one account. Never used as identity. */
  accountLabel: string;
  /**
   * Execution readiness from the server's account context. Never taken from a
   * portfolio read: saved-evidence reads carry no execution readiness.
   */
  readiness: UsReadiness;
  /** Injectable clock for the repeat-cancel quiet interval. */
  clock?: () => number;
  /** Re-read this often while the page is visible; absent = read on demand
   * only. A read or action already in flight is never doubled. */
  autoRefreshMs?: number;
  /** R8-F1: ask before a row's Cancel or Repeat cancel (the host's
   * confirm-before-cancel preference, as international rows honor it). One
   * request after the confirmation; Back sends nothing. Absent = no dialog. */
  confirmCancel?: boolean;
  /** A new value re-reads the same selection: the host changed orders
   * elsewhere. It reads the newest page of orders; the rows on screen, row
   * errors and an action in flight stay until that read lands. */
  refreshSignal?: number;
};

/** How many rows a filter hid or could not classify yet. Exported for Org
 * Detail's sport filter (#1202), which classifies by sport only (`what`). */
export function FilterNote({ hidden, pending, noun, what = 'sport or market type', testId = `us-filter-note-${noun}` }: {
  hidden: number; pending: number; noun: string; what?: string; testId?: string;
}) {
  if (hidden === 0 && pending === 0) return null;
  const parts = [
    hidden > 0 ? `${hidden} ${noun}${hidden === 1 ? '' : 's'} hidden by your filters` : null,
    pending > 0 ? `${pending} unclassified — ${what} not known yet, so ${pending === 1 ? 'it stays' : 'they stay'} visible` : null,
  ].filter(Boolean);
  return <p className="text-muted-foreground px-4 pt-1 text-xs" data-testid={testId}>{parts.join(' · ')}</p>;
}

/** One selection of account + view. A new object on every switch, A -> B -> A included. */
type Visit = { scope: UsScope; view: 'own' | 'desk'; refreshSignal: number };

/**
 * The latest read, tagged with the visit it belongs to. `snapshot` is the
 * last one accepted in that visit: a refresh keeps it on screen (a failed
 * refresh shows it as stale). Stored state from any other visit is not shown
 * at all, so B never shows A's rows and a new visit reads as loading without
 * resetting state in an effect.
 */
type ReadState = {
  snapshot: UsPortfolioSnapshot | null; loading: boolean; error: string | null; after?: string;
  /** Intent ids that finished under the trader's eyes in this read (working
   * in the previous one, terminal now): shown once more with their terminal
   * word, so the outcome of a cancel is seen at the control. */
  keep?: ReadonlySet<string>;
};
type StoredRead = ReadState & { visit: Visit | null };
type RowErrors = { visit: Visit | null; map: ReadonlyMap<string, string> };
const NO_ERRORS: ReadonlyMap<string, string> = new Map();
const NEW_VISIT: ReadState = { snapshot: null, loading: true, error: null };

/** Same account scope and view, told only to re-read: the rows on screen
 * stay while it reads, and stay if that read fails. Any other change is a
 * new selection and starts empty. */
const refreshOf = (prev: Visit | null, next: Visit) => prev !== null && prev.refreshSignal !== next.refreshSignal
  && prev.view === next.view && isSameUsScope(prev.scope, next.scope);
/** The same selection: this visit, or one only told to re-read. A row action,
 * its error and the page cursor carry across a refresh signal; anything else
 * belongs to an earlier selection and never paints. */
const sameSelection = (a: Visit | null, b: Visit | null) => a === b || (a !== null && b !== null && refreshOf(a, b));

/** The one operation in flight, owned by the visit that started it. */
type Pending = { key: string; kind: UsOrderOperation; visit: Visit | null };

/** Orders working in `before` that `after` shows finished (plus `carry`, the
 * ones already being shown once more). Empty when nothing just finished.
 * Shared with the Tracker's US readers (`usTrackerFeed.tsx`). */
export function finishedSince(before: UsPortfolioSnapshot | null, after: UsPortfolioSnapshot, carry?: ReadonlySet<string>): ReadonlySet<string> | undefined {
  if (!before) return undefined;
  const working = new Set(before.orders.filter((r) => !presentUsOrder(r).terminal).map((r) => r.intentId));
  const out = new Set<string>();
  for (const r of after.orders) if (presentUsOrder(r).terminal && (working.has(r.intentId) || carry?.has(r.intentId))) out.add(r.intentId);
  return out.size ? out : undefined;
}

function failureReason(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error && typeof error.code === 'string') return error.code;
  return error instanceof Error ? error.message : 'US_READ_UNAVAILABLE';
}

export function UsPortfolioPanel({
  adapter,
  scope,
  view = 'own',
  accountLabel,
  readiness,
  clock = Date.now,
  autoRefreshMs,
  confirmCancel = false,
  refreshSignal = 0,
}: UsPortfolioPanelProps) {
  // A visit changes on every switch, including A -> B -> A, where the server
  // revision may not change: any read or action started in an earlier visit
  // can never paint into this one. It is rebuilt from values, so a parent
  // re-rendering with an equal but new scope object is not a new visit.
  const { venue, scopeKey, revision } = scope;
  const visit = useMemo<Visit>(
    () => ({ scope: { venue, scopeKey, revision }, view, refreshSignal }),
    [venue, scopeKey, revision, view, refreshSignal],
  );
  /** The visit whose read the effect last started; results are checked against it. */
  const currentVisit = useRef<Visit | null>(null);
  // Each read gets a generation; only the latest one may paint.
  const generation = useRef(0);

  const [stored, setRead] = useState<StoredRead>({ visit: null, ...NEW_VISIT });
  const read: ReadState = stored.visit === visit ? stored
    : refreshOf(stored.visit, visit) && stored.snapshot ? { ...stored, loading: true, error: null } : NEW_VISIT;

  // One operation at a time (cancel, repeat, status or refresh). The ref
  // closes the double-click window before React re-renders the disabled
  // buttons; the state drives what is shown.
  const inFlight = useRef<Pending | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [storedErrors, setErrors] = useState<RowErrors>({ visit: null, map: NO_ERRORS });
  const errors = sameSelection(storedErrors.visit, visit) ? storedErrors.map : NO_ERRORS;
  // R8-F1: the cancel awaiting the trader's confirmation, owned by the visit
  // that asked; another selection drops it (its row is no longer on screen).
  const [askedCancel, setAskedCancel] = useState<{ kind: 'cancel' | 'repeat'; row: UsOrderRow; visit: Visit } | null>(null);
  const pendingConfirm = askedCancel && sameSelection(askedCancel.visit, visit) ? askedCancel : null;
  const [nowMs, setNowMs] = useState(clock);

  /**
   * Read `v`'s portfolio. Sets state only when the reply lands: a new visit
   * already reads as loading, and Refresh marks itself loading before calling.
   */
  const readPortfolio = useCallback(
    (v: Visit, after?: string) => {
      const mine = ++generation.current;
      const kept = (prev: StoredRead) => (prev.visit === v || refreshOf(prev.visit, v) ? prev.snapshot : null);
      const request = after === undefined ? adapter.readPortfolio(v.scope)
        : adapter.readOrderPage?.(v.scope, after) ?? Promise.reject(new Error('US_PAGINATION_UNAVAILABLE'));
      return request.then(
        (snapshot) => {
          if (mine !== generation.current) return;
          // The read must be for this exact scope AND view: desk rows must never
          // appear under "Your orders", or the reverse.
          const mismatch = !acceptUsSnapshot(v.scope, snapshot)
            ? 'US_READ_SCOPE_MISMATCH'
            : snapshot.readDetails && snapshot.readDetails.view !== v.view
              ? 'US_READ_VIEW_MISMATCH'
              : null;
          if (!mismatch) setRead((prev) => ({ visit: v, snapshot, loading: false, error: null, after, keep: finishedSince(kept(prev), snapshot) }));
          else setRead((prev) => ({ visit: v, snapshot: kept(prev), loading: false, error: mismatch,
            after: sameSelection(prev.visit, v) ? prev.after : undefined }));
        },
        (error: unknown) => {
          if (mine !== generation.current) return;
          const code = failureReason(error);
          // A reply for a superseded read returned above; any refusal that
          // reaches here (context changed included) is this visit's failure.
          setRead((prev) => ({ visit: v, snapshot: kept(prev), loading: false, error: code,
            after: sameSelection(prev.visit, v) ? prev.after : undefined }));
        },
      );
    },
    [adapter],
  );

  // Every new read bumps the generation, so a reply from the previous visit
  // can no longer paint; after unmount React ignores the update anyway.
  useEffect(() => {
    currentVisit.current = visit;
    void readPortfolio(visit);
  }, [visit, readPortfolio]);

  const release = useCallback(
    (op: Pending) => {
      if (inFlight.current === op) {
        inFlight.current = null;
        setPending(null);
      }
      setNowMs(clock());
    },
    [clock],
  );

  const refresh = useCallback((after: string | null | undefined = read.after) => {
    if (inFlight.current && sameSelection(inFlight.current.visit, currentVisit.current)) return;
    const op: Pending = { key: '', kind: 'refresh', visit: currentVisit.current };
    inFlight.current = op;
    setPending(op);
    setRead((prev) => (prev.visit === visit ? { ...prev, loading: true, error: null } : prev));
    void readPortfolio(visit, after ?? undefined).finally(() => release(op));
  }, [readPortfolio, release, visit, read.after]);

  // A quiet re-read on the host's cadence while the page is visible. It never
  // doubles a read or action in flight, and the rows, row errors and an
  // action in flight stay on screen through it.
  useEffect(() => {
    if (!autoRefreshMs || autoRefreshMs <= 0) return;
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      refresh();
    }, autoRefreshMs);
    return () => clearInterval(timer);
  }, [autoRefreshMs, refresh]);

  const execute = useCallback(
    (kind: UsOrderOperation, row: UsOrderRow) => {
      if (inFlight.current && sameSelection(inFlight.current.visit, currentVisit.current)) return;
      if (kind === 'refresh' || (!row.orderId && kind !== 'status')) return;
      const key = usOrderKey(row);
      const op: Pending = { key, kind, visit: currentVisit.current };
      inFlight.current = op;
      setPending(op);
      const startedIn = currentVisit.current;
      const request = { scope: row.scope, intentId: row.intentId };
      const call =
        kind === 'status'
          ? adapter.checkOrderStatus(request).then((order) => (s: UsPortfolioSnapshot) => applyUsOrderStatus(s, order))
          : (kind === 'cancel'
              ? adapter.cancelOrder({ ...request, orderId: row.orderId! })
              : adapter.repeatCancel({ ...request, orderId: row.orderId!, previousAttemptId: row.cancel!.attemptId })
            ).then((result) => (s: UsPortfolioSnapshot) => applyUsCancelResult(s, result));
      call
        .then(
          (apply) => {
            // Both writes are tagged with the visit that asked; a later visit
            // never shows them.
            setRead((prev) => {
              if (!sameSelection(prev.visit, startedIn) || !prev.snapshot) return prev;
              const next = apply(prev.snapshot);
              return { ...prev, snapshot: next, keep: finishedSince(prev.snapshot, next, prev.keep) };
            });
            setErrors((prev) => {
              if (!sameSelection(prev.visit, startedIn) || !prev.map.has(key)) return prev;
              const map = new Map(prev.map);
              map.delete(key);
              return { visit: prev.visit, map };
            });
          },
          (error: unknown) => {
            if (!sameSelection(currentVisit.current, startedIn)) return;
            const code = failureReason(error);
            // The row stays exactly as it was: an error never erases it.
            setErrors((prev) => ({
              visit: startedIn,
              map: new Map(sameSelection(prev.visit, startedIn) ? prev.map : NO_ERRORS).set(key, code),
            }));
          },
        )
        .finally(() => release(op));
    },
    [adapter, release],
  );
  // R8-F1: a cancel asks first when the host says so; everything else, and a
  // confirmed cancel, goes straight to `execute`. The same lock applies: an
  // operation already in flight is not asked about.
  const run = useCallback(
    (kind: UsOrderOperation, row: UsOrderRow) => {
      if (confirmCancel && (kind === 'cancel' || kind === 'repeat') && row.orderId) {
        if (inFlight.current && sameSelection(inFlight.current.visit, currentVisit.current)) return;
        setAskedCancel({ kind, row, visit });
        return;
      }
      execute(kind, row);
    },
    [confirmCancel, execute, visit],
  );

  // Wake exactly when the next repeat cancel unlocks; no ticking otherwise.
  // (Each locked button counts down on its own, #1113.)
  const orders = read.snapshot?.orders ?? null;
  useEffect(() => {
    if (!orders) return;
    const now = clock();
    const next = orders
      .map((o) => usCancelView(o, readiness, now).repeatAvailableAtMs)
      .filter((at): at is number => at !== null && at > now)
      .sort((a, b) => a - b)[0];
    if (next === undefined) return;
    const timer = setTimeout(() => setNowMs(clock()), next - now);
    return () => clearTimeout(timer);
  }, [orders, readiness, clock, nowMs]);

  // An action still settling from an earlier visit does not lock this one: a
  // new selection invalidates its browser result (Connection rule 7).
  const mine = pending && sameSelection(pending.visit, visit) ? pending : null;
  const busy = mine !== null || read.loading;
  // A delayed read can arrive after a repeat deadline, with no future timer to
  // wake us. Sample this render rather than reusing the last timer/action time.
  const actions: UsOrderActions = { readiness, nowMs: clock(), pending: mine, busy, errors, run, clock };
  const { snapshot } = read;
  // What the list shows: working orders only (a filled, canceled, expired or
  // rejected order is history, as the K/P Open Orders lists hold it), and
  // holdings with something in them (a known-zero holding, as the rail hides
  // it; and the row the relay projects for an order that has never cleared
  // anything, see `usPhantomHolding`). A row that finished under the trader's
  // eyes (a cancel answered "filled", a fill won the race) stays for this read
  // with its terminal word, so the outcome of their action is seen at the
  // control; the next read lets it go.
  const keep = read.keep;
  const workingOrders = useMemo(() => (snapshot?.orders ?? [])
    .filter((r) => !presentUsOrder(r).terminal || usOrderNeedsFillVisibility(r) || keep?.has(r.intentId) || errors.has(usOrderKey(r))), [snapshot, keep, errors]);
  const openPositions = useMemo(() => (snapshot?.positions ?? []).filter((r) => !usKnownZeroHolding(r) && !usPhantomHolding(r)), [snapshot]);
  const sections = snapshot ? usSectionCoverage(snapshot, view) : null;

  // A read that failed with nothing on screen: one plain line, the code in
  // its hover, and the panel's own Retry (the K/P lists say "unavailable" the
  // same way). A failed refresh keeps the rows and says so beside them.
  const readFailed = read.error && !snapshot;
  const stale = read.error && snapshot;
  return (
    <section
      aria-label={`${VENUE_NAME.polymarket_us} portfolio`}
      data-testid="us-portfolio"
      className="bg-card border-border text-foreground rounded-lg border"
    >
      {read.loading && !snapshot && (
        <p className="text-muted-foreground px-4 py-3 text-xs" data-testid="us-portfolio-state">Reading…</p>
      )}
      {readFailed && (
        <p className="text-muted-foreground flex flex-wrap items-center gap-2 px-4 py-3 text-xs" role="alert" data-testid="us-portfolio-state">
          <span title={read.error ?? undefined}>Polymarket US orders and holdings unavailable.</span>
          <button type="button" className={ACTION_BUTTON} aria-disabled={busy || undefined} onClick={() => { if (!busy) refresh(); }}>Retry</button>
        </p>
      )}
      {snapshot && (
        <div>
          <div className="flex items-center gap-2 px-4 pt-3">
            <h3 className="text-muted-foreground text-[11px] font-semibold uppercase tracking-wider">Open orders</h3>
            {stale && <span className="text-xs text-amber-400 light:text-amber-700" title={read.error ?? undefined} data-testid="us-portfolio-stale">
              refresh failed; showing the last read
            </span>}
          </div>
          <UsOrderTable orders={workingOrders} actions={actions} />
          {(read.after !== undefined || (snapshot.readDetails?.nextOrderCursor && adapter.readOrderPage)) && (
            <nav aria-label="US order pages" className="flex flex-wrap items-center gap-2 px-4 py-2 text-xs">
              {read.after !== undefined && <button type="button" className={ACTION_BUTTON}
                aria-disabled={busy || undefined} onClick={() => { if (!busy) refresh(null); }}>Newest orders</button>}
              {snapshot.readDetails?.nextOrderCursor && adapter.readOrderPage && (
                <button type="button" className={ACTION_BUTTON} aria-disabled={busy || undefined}
                  title="Older saved orders are kept on further pages"
                  onClick={() => { if (!busy) refresh(snapshot.readDetails!.nextOrderCursor!); }}>Older orders</button>
              )}
            </nav>
          )}
        </div>
      )}
      {snapshot && (
        <div className="border-border border-t">
          <h3 className="text-muted-foreground px-4 pt-3 text-[11px] font-semibold uppercase tracking-wider">Open positions</h3>
          <UsPositionCards positions={openPositions} holdings={sections?.holdings ?? null} />
        </div>
      )}
      {/* R8-F1: the side rail's US confirmation, for this panel's rows. */}
      <ConfirmModal
        isOpen={pendingConfirm !== null}
        onClose={() => setAskedCancel(null)}
        onConfirm={() => {
          const target = pendingConfirm;
          setAskedCancel(null);
          if (target) execute(target.kind, target.row);
        }}
        title="Cancel order?"
        message={pendingConfirm ? usCancelConfirmText(pendingConfirm.kind, pendingConfirm.row, accountLabel) : 'Cancel this order?'}
        confirmLabel="Cancel order"
        cancelLabel="Back"
        variant="danger"
      />
    </section>
  );
}

/** "0", "-0.000": a holding known to be empty (the side rail's rule). Unknown
 * (null) is not zero. */
export function usKnownZeroHolding(row: Pick<UsPositionRow, 'heldQuantity'>): boolean {
  return row.heldQuantity !== null && /^-?0+(?:\.0+)?$/.test(row.heldQuantity);
}

/**
 * A relay before #1230 projected a holding row for a [market, outcome] the
 * moment an order was recorded for it, before anything had cleared. That row
 * had no established size, nothing cleared and no allocation: it was the
 * order, not a holding, and the Open Positions list does not show it (the
 * Kalshi and international lists show a position only once a fill exists).
 * The relay now emits no such row, so on current data this is a no-op; it
 * stays as the guard for an older relay. A holding with any cleared quantity,
 * an opening line or an established size stays, size unknown or not.
 */
export function usPhantomHolding(row: Pick<UsPositionRow, 'heldQuantity' | 'knownContribution' | 'allocation'>): boolean {
  if (row.heldQuantity !== null) return false;
  const zero = (text: string | undefined) => text === undefined || decimalUnits(text) === 0n;
  // Nothing has ever happened to this holding: no opening line, nothing
  // cleared, nothing settled. A recorded opening of zero is the same as none.
  const a = row.allocation;
  return zero(row.knownContribution) && (!a || (zero(a.openingQuantity) && zero(a.settledQuantity)));
}

/** The confirmation's one line, in the Tracker's words (`Cancel {label}?`):
 * the working size and price, the account only where it can differ. A repeat
 * says it is another cancel. */
export function usCancelConfirmText(kind: 'cancel' | 'repeat', row: UsOrderRow, accountLabel: string): string {
  const view = presentUsOrder(row);
  const size = view.remaining === UNAVAILABLE ? row.quantity : view.remaining;
  const verb = kind === 'repeat' ? 'Send another cancel for' : 'Cancel';
  const price = usPriceCentsLabel(view.price);
  return `${verb} ${row.action.toUpperCase()} ${row.target.outcome.toUpperCase()} ${size} @ ${price} — ${
    row.target.label}${accountLabel ? ` (${accountLabel})` : ''}?`;
}
